import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { SECTION_PERMISSIONS } from "@/config/permissions";
import { buildApprovalSubmissionFeedback } from "@/features/approvals/approval-utils";
import type { PermissionCode } from "@/features/auth/permissions";
import { usePermissions } from "@/features/auth/usePermissions";
import { extractApiError } from "@/lib/api-error";
import { createLogger } from "@/lib/logger";
import {
    useUserPresenceLookup,
    type UserPresenceConnectionState,
    type UserPresenceSnapshot,
} from "@/lib/realtime/userPresence";
import {
    ArrowLeftIcon,
    EnvelopeIcon,
    PhoneIcon,
    IdentificationIcon,
    CheckCircleIcon,
    XCircleIcon,
    CalendarIcon,
    ClockIcon,
    UserCircleIcon,
    ShieldCheckIcon,
    BuildingOfficeIcon,
    BriefcaseIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    TruckIcon,
    MapPinIcon,
    CurrencyDollarIcon,
    StarIcon,
    XMarkIcon,
    TicketIcon,
    WalletIcon,
    PaperAirplaneIcon,
    ArrowUpIcon,
    ArrowDownIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    NoSymbolIcon,
    PowerIcon,
    TrashIcon,
} from "@heroicons/react/24/outline";
import { StarIcon as StarSolid } from "@heroicons/react/24/solid";
import {
    getDriverDetails,
    getDriverRides,
    reviewDriverRoadWorthiness,
    getDriverTrips,
    getDriverWallet,
    reactivateDriver,
    suspendDriver,
    banDriver,
} from "../api/driversApi";
import { Button } from "@/components/ui/Button";
import { sendPushNotificationToUser } from "@/features/notifications/api/messagingApi";
import type {
    DriverDetails,
    DriverRide,
    DriverRideSummary,
    DriverTrip,
    DriverTripSummary,
    DriverPayout,
    DriverWalletSummary,
    RoadWorthinessReviewAction,
} from "../types";

const log = createLogger("DriverDetails");

const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });
};

const formatShortDate = (dateString: string | null) => {
    if (!dateString) return "\u2014";
    return new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
    });
};

const formatCurrency = (amount: number | null, currency = "NGN") => {
    if (amount === null || amount === undefined) return "\u2014";
    return new Intl.NumberFormat("en-NG", { style: "currency", currency }).format(amount);
};

const formatEnumLabel = (value: string | null) => {
    if (!value) return "\u2014";
    return value
        .toLowerCase()
        .split("_")
        .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
        .join(" ");
};

const formatRelativeTime = (dateString: string | null) => {
    if (!dateString) return "\u2014";

    const timestamp = new Date(dateString).getTime();

    if (Number.isNaN(timestamp)) {
        return "\u2014";
    }

    const elapsedMs = Math.max(Date.now() - timestamp, 0);
    const elapsedMinutes = Math.floor(elapsedMs / 60_000);

    if (elapsedMinutes < 1) return "Just now";
    if (elapsedMinutes < 60) {
        return `${elapsedMinutes} min${elapsedMinutes === 1 ? "" : "s"} ago`;
    }

    const elapsedHours = Math.floor(elapsedMinutes / 60);

    if (elapsedHours < 24) {
        return `${elapsedHours} hour${elapsedHours === 1 ? "" : "s"} ago`;
    }

    const elapsedDays = Math.floor(elapsedHours / 24);

    if (elapsedDays < 7) {
        return `${elapsedDays} day${elapsedDays === 1 ? "" : "s"} ago`;
    }

    return formatDate(dateString);
};

const formatCoordinates = (latitude: number | null, longitude: number | null) => {
    if (latitude === null || longitude === null) return "Unavailable";
    return `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
};

const getVehicleImageSlots = (vehicle: DriverDetails["driverProfile"]["vehicle"]) => {
    const legacyImages = vehicle.vehicleImages ?? [];

    return [
        {
            key: "front",
            label: "Front View",
            url: vehicle.frontImage ?? legacyImages[0] ?? null,
        },
        {
            key: "side",
            label: "Side View",
            url: vehicle.sideImage ?? legacyImages[1] ?? null,
        },
        {
            key: "rear",
            label: "Rear View",
            url: vehicle.backImage ?? legacyImages[2] ?? null,
        },
    ].filter((image): image is { key: string; label: string; url: string } => Boolean(image.url));
};

const StatusBadge = ({ status }: { status: string }) => {
    const statusStyles: Record<string, string> = {
        ACTIVE: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        INACTIVE: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
        SUSPENDED: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
        DELETED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
        BANNED: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
        VERIFIED: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        APPROVED: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        PENDING: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
        REJECTED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
        COMPLETED: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        CANCELLED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
        CONFIRMED: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        IN_PROGRESS: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        AVAILABLE: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        SUCCESS: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        FAILED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
    };

    return (
        <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${statusStyles[status] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
            {status?.replace(/_/g, " ")}
        </span>
    );
};

const VerificationBadge = ({ verified, label }: { verified: boolean; label: string }) => (
    <div className="flex items-center gap-2">
        {verified ? (
            <CheckCircleIcon className="h-5 w-5 text-emerald-500" />
        ) : (
            <XCircleIcon className="h-5 w-5 text-slate-400" />
        )}
        <span className={`text-sm font-medium ${verified ? "text-emerald-700 dark:text-emerald-400" : "text-slate-500 dark:text-slate-400"}`}>
            {label}
        </span>
    </div>
);

const PresencePanel = ({
    presence,
    connectionState,
    error,
    lastUpdatedAt,
    onRefresh,
}: {
    presence: UserPresenceSnapshot | null;
    connectionState: UserPresenceConnectionState;
    error: string | null;
    lastUpdatedAt: string | null;
    onRefresh: () => void;
}) => {
    const isOnline =
        connectionState === "connected" && presence?.onlineStatus === "ONLINE";
    const isConnecting = connectionState === "connecting";
    const statusTone = isOnline
        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
        : isConnecting
          ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
          : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300";

    const summary = isOnline
        ? "Present and live"
        : presence
          ? presence.onlineStatus === "ONLINE"
            ? "Last reported online"
            : "Not currently active"
          : isConnecting
            ? "Checking live presence..."
            : "Presence unavailable";

    return (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="flex flex-col gap-4 border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                        User Presence
                    </h2>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        Realtime mobile presence while this page stays open.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${statusTone}`}>
                        {isOnline ? (
                            <span className="relative flex h-3 w-3">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                                <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500" />
                            </span>
                        ) : (
                            <span
                                className={`inline-flex h-3 w-3 rounded-full ${isConnecting ? "bg-amber-500" : "bg-slate-400 dark:bg-slate-500"}`}
                            />
                        )}
                        <span>{isOnline ? "Live" : presence?.onlineStatus ? formatEnumLabel(presence.onlineStatus) : isConnecting ? "Checking" : "Unavailable"}</span>
                    </div>
                    <button
                        onClick={onRefresh}
                        disabled={connectionState !== "connected"}
                        className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                    >
                        Refresh
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-6 bg-gradient-to-r from-emerald-500/[0.04] via-transparent to-transparent p-6 md:grid-cols-4">
                <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Presence
                    </p>
                    <p
                        className={`mt-2 text-sm font-semibold ${
                            isOnline
                                ? "text-emerald-600 dark:text-emerald-400"
                                : "text-slate-900 dark:text-slate-100"
                        }`}
                    >
                        {summary}
                    </p>
                </div>
                <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        App State
                    </p>
                    <p className="mt-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {isOnline ? formatEnumLabel(presence?.appState ?? null) : "\u2014"}
                    </p>
                </div>
                <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Last Seen
                    </p>
                    <p className="mt-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {isOnline
                            ? "Live now"
                            : presence?.lastSeenAt
                              ? formatDate(presence.lastSeenAt)
                              : "\u2014"}
                    </p>
                    {!isOnline && presence?.lastSeenAt && (
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                            {formatRelativeTime(presence.lastSeenAt)}
                        </p>
                    )}
                </div>
                <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Location
                    </p>
                    <p className="mt-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {formatCoordinates(presence?.latitude ?? null, presence?.longitude ?? null)}
                    </p>
                    {presence?.latitude !== null && presence?.longitude !== null && (
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                            Latest mobile coordinates
                        </p>
                    )}
                </div>
            </div>

            <div className="flex flex-col gap-2 border-t border-slate-200 px-6 py-3 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400 sm:flex-row sm:items-center sm:justify-between">
                <span>
                    {error
                        ? error
                        : lastUpdatedAt
                          ? `Last refreshed ${formatRelativeTime(lastUpdatedAt)}`
                          : "Waiting for first presence reply."}
                </span>
                <span>
                    Socket: {formatEnumLabel(connectionState)}
                    {presence?.role ? ` · ${formatEnumLabel(presence.role)}` : ""}
                </span>
            </div>
        </div>
    );
};

// ============================================================================
// Shared UI Components
// ============================================================================

const Pagination = ({
    currentPage,
    totalPages,
    totalElements,
    onPageChange,
}: {
    currentPage: number;
    totalPages: number;
    totalElements: number;
    onPageChange: (page: number) => void;
}) => {
    if (totalPages <= 1) return null;
    return (
        <div className="flex items-center justify-between border-t border-slate-200 px-6 py-4 dark:border-slate-800">
            <p className="text-sm text-slate-600 dark:text-slate-400">
                Page {currentPage + 1} of {totalPages} &middot; {totalElements} total
            </p>
            <div className="flex gap-2">
                <button
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 0}
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                    <ChevronLeftIcon className="h-4 w-4" /> Prev
                </button>
                <button
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages - 1}
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                    Next <ChevronRightIcon className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
};

const EmptyState = ({ icon: Icon, title, subtitle }: { icon: React.ElementType; title: string; subtitle: string }) => (
    <div className="flex h-48 flex-col items-center justify-center text-center">
        <Icon className="mb-3 h-10 w-10 text-slate-300 dark:text-slate-600" />
        <p className="text-sm font-medium text-slate-600 dark:text-slate-400">{title}</p>
        <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">{subtitle}</p>
    </div>
);

const LoadingSpinner = () => (
    <div className="flex h-48 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
    </div>
);

const ErrorState = ({ message }: { message: string }) => (
    <div className="flex h-48 items-center justify-center text-rose-500">
        <p className="text-sm">{message}</p>
    </div>
);

const InlineError = ({ message }: { message: string }) =>
    message ? (
        <div className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-400">
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{message}</span>
        </div>
    ) : null;

const ModalOverlay = ({
    children,
    onClose,
}: {
    children: React.ReactNode;
    onClose: () => void;
}) => (
    <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        onClick={(event) => event.target === event.currentTarget && onClose()}
    >
        {children}
    </div>
);

const ModalCard = ({ children }: { children: React.ReactNode }) => (
    <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-xl dark:border-slate-700 dark:bg-slate-800">
        {children}
    </div>
);

const RatingStars = ({ rating }: { rating: number | null }) => {
    if (rating === null || rating === undefined) return <span className="text-xs text-slate-400">No rating</span>;
    return (
        <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((star) =>
                star <= Math.round(rating) ? (
                    <StarSolid key={star} className="h-4 w-4 text-amber-400" />
                ) : (
                    <StarIcon key={star} className="h-4 w-4 text-slate-300 dark:text-slate-600" />
                )
            )}
            <span className="ml-1 text-xs font-medium text-slate-600 dark:text-slate-400">
                {rating.toFixed(1)}
            </span>
        </div>
    );
};

// ============================================================================
// Tab Config
// ============================================================================

type TabKey = "profile" | "rides" | "trips" | "wallet";

const TABS: {
    key: TabKey;
    label: string;
    icon: React.ElementType;
    permissions?: readonly PermissionCode[];
}[] = [
    { key: "profile", label: "Profile", icon: UserCircleIcon },
    { key: "rides", label: "Rides", icon: TruckIcon, permissions: SECTION_PERMISSIONS.DRIVER_RIDES },
    { key: "trips", label: "Trips", icon: TicketIcon, permissions: SECTION_PERMISSIONS.DRIVER_TRIPS },
    {
        key: "wallet",
        label: "Wallet",
        icon: WalletIcon,
        permissions: [
            ...SECTION_PERMISSIONS.DRIVER_WALLET,
            ...SECTION_PERMISSIONS.DRIVER_PAYOUTS_VIEW,
        ],
    },
];

// ============================================================================
// Profile Tab
// ============================================================================

const ProfileTab = ({
    driver,
    profile,
    setSelectedImage,
    canReviewRoadWorthiness,
    canViewPayoutInfo,
    onRoadWorthinessReview,
    roadWorthinessReviewError
}: {
    driver: DriverDetails;
    profile: DriverDetails["driverProfile"];
    setSelectedImage: (url: string | null) => void;
    canReviewRoadWorthiness: boolean;
    canViewPayoutInfo: boolean;
    onRoadWorthinessReview: (
        action: RoadWorthinessReviewAction,
        reason?: string
    ) => Promise<unknown>;
    roadWorthinessReviewError: string | null;
}) => {
    const [reviewReason, setReviewReason] = useState("");
    const [reviewValidationError, setReviewValidationError] = useState<string | null>(null);
    const [submittingReviewAction, setSubmittingReviewAction] =
        useState<RoadWorthinessReviewAction | null>(null);
    const driverLicenseNumber = profile?.driverLicense?.licenseNumber ?? profile?.licenseNumber ?? null;
    const isDriverLicenseVerified = profile?.driverLicense?.isValid ?? false;
    const vehicleImageSlots = getVehicleImageSlots(profile.vehicle);

    const submitRoadWorthinessReview = async (action: RoadWorthinessReviewAction) => {
        const trimmedReason = reviewReason.trim();
        if (action === "REJECT" && !trimmedReason) {
            setReviewValidationError("Reason is required when rejecting road worthiness.");
            return;
        }

        setReviewValidationError(null);
        setSubmittingReviewAction(action);

        try {
            await onRoadWorthinessReview(action, trimmedReason || undefined);
            setReviewReason("");
        } catch {
            // Error feedback is surfaced via roadWorthinessReviewError from the mutation.
        } finally {
            setSubmittingReviewAction(null);
        }
    };

    return (
        <>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Contact Information */}
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Contact Information</h2>
                </div>
                <div className="space-y-4 p-6">
                    <div className="flex items-start gap-4">
                        <EnvelopeIcon className="mt-0.5 h-5 w-5 text-slate-400" />
                        <div className="flex-1">
                            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Email Address</p>
                            <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">{driver.email}</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-4">
                        <PhoneIcon className="mt-0.5 h-5 w-5 text-slate-400" />
                        <div className="flex-1">
                            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Phone Number</p>
                            <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">{driver.phoneNumber}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Verification Status */}
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Verification Status</h2>
                </div>
                <div className="space-y-4 p-6">
                    <VerificationBadge verified={driver.emailVerified} label="Email Verified" />
                    <VerificationBadge verified={driver.phoneVerified} label="Phone Verified" />
                    <VerificationBadge verified={isDriverLicenseVerified} label="Driver License Valid" />
                    <VerificationBadge verified={driver.livenessChecked} label="Liveness Checked" />
                    {driverLicenseNumber && (
                        <div className="flex items-start gap-4 pt-2">
                            <IdentificationIcon className="mt-0.5 h-5 w-5 text-slate-400" />
                            <div className="flex-1">
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Driver License Number</p>
                                <p className="mt-1 font-mono text-sm font-semibold text-slate-900 dark:text-slate-100">{driverLicenseNumber}</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Driver License Information */}
            {profile && (
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Driver License</h2>
                    </div>
                    <div className="space-y-4 p-6">
                        <div className="flex items-start gap-4">
                            <IdentificationIcon className="mt-0.5 h-5 w-5 text-slate-400" />
                            <div className="flex-1">
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">License Number</p>
                                <p className="mt-1 font-mono text-sm font-semibold text-slate-900 dark:text-slate-100">{profile.licenseNumber}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <TruckIcon className="mt-0.5 h-5 w-5 text-slate-400" />
                            <div className="flex-1">
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">License Plate</p>
                                <p className="mt-1 font-mono text-sm font-semibold text-slate-900 dark:text-slate-100">{profile.licensePlate}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <CalendarIcon className="mt-0.5 h-5 w-5 text-slate-400" />
                            <div className="flex-1">
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Expiry Date</p>
                                <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">{formatDate(profile.licenseExpiryDate)}</p>
                            </div>
                        </div>
                        {profile.driverLicense && (
                            <>
                                <div className="grid grid-cols-2 gap-4 pt-2">
                                    <div>
                                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Issue Date</p>
                                        <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">{formatDate(profile.driverLicense.issueDate)}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Expiry Date</p>
                                        <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">{formatDate(profile.driverLicense.expiryDate)}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 pt-2">
                                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${profile.driverLicense.isValid ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" : "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400"}`}>
                                        {profile.driverLicense.isValid ? "Valid" : "Invalid"}
                                    </span>
                                </div>
                                {profile.driverLicense.documentUrl && (
                                    <div className="pt-2">
                                        <a href={profile.driverLicense.documentUrl} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-primary-600 hover:text-primary-500">
                                            View License Document →
                                        </a>
                                    </div>
                                )}
                            </>
                        )}
                        {profile.driverLicenseImage && !profile.driverLicense && (
                            <div className="pt-2">
                                <a href={profile.driverLicenseImage} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-primary-600 hover:text-primary-500">
                                    View License Image →
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Road Worthiness Information */}
            {profile && profile.roadWorthiness && (
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Road Worthiness</h2>
                    </div>
                    <div className="space-y-4 p-6">
                        <div className="flex items-start gap-4">
                            <IdentificationIcon className="mt-0.5 h-5 w-5 text-slate-400" />
                            <div className="flex-1">
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Certificate Number</p>
                                <p className="mt-1 font-mono text-sm font-semibold text-slate-900 dark:text-slate-100">{profile.roadWorthiness.certificateNumber}</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Issue Date</p>
                                <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">{formatDate(profile.roadWorthiness.issueDate)}</p>
                            </div>
                            <div>
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Expiry Date</p>
                                <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">{formatDate(profile.roadWorthiness.expiryDate)}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${profile.roadWorthiness.isValid ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" : "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400"}`}>
                                {profile.roadWorthiness.isValid ? "Valid" : "Invalid"}
                            </span>
                        </div>
                        <div>
                            <p className="mb-2 text-xs font-medium text-slate-500 dark:text-slate-400">Approval Status</p>
                            <StatusBadge status={profile.roadWorthiness.approvalStatus || "PENDING"} />
                        </div>
                        {profile.roadWorthiness.documentUrl && (
                            <div className="pt-2">
                                <a href={profile.roadWorthiness.documentUrl} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-primary-600 hover:text-primary-500">
                                    View Road Worthiness Document →
                                </a>
                            </div>
                        )}
                        {canReviewRoadWorthiness ? (
                            <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/40">
                                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Review Action</p>
                                <div>
                                    <label
                                        htmlFor="road-worthiness-review-reason"
                                        className="mb-1.5 block text-xs font-medium text-slate-500 dark:text-slate-400"
                                    >
                                        Reason (optional for approve, required for reject)
                                    </label>
                                    <textarea
                                        id="road-worthiness-review-reason"
                                        rows={3}
                                        value={reviewReason}
                                        onChange={(e) => {
                                            setReviewReason(e.target.value);
                                            if (reviewValidationError) setReviewValidationError(null);
                                        }}
                                        placeholder="Enter review reason"
                                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                    />
                                </div>
                                {(reviewValidationError || roadWorthinessReviewError) && (
                                    <p className="text-xs font-medium text-rose-600 dark:text-rose-400">
                                        {reviewValidationError || roadWorthinessReviewError}
                                    </p>
                                )}
                                <div className="flex flex-wrap gap-2">
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={() => void submitRoadWorthinessReview("APPROVE")}
                                        disabled={submittingReviewAction !== null}
                                    >
                                        {submittingReviewAction === "APPROVE" ? "Approving..." : "Approve"}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => void submitRoadWorthinessReview("REJECT")}
                                        disabled={submittingReviewAction !== null}
                                        className="border-rose-300 text-rose-700 hover:bg-rose-50 dark:border-rose-700 dark:text-rose-400 dark:hover:bg-rose-950/30"
                                    >
                                        {submittingReviewAction === "REJECT" ? "Rejecting..." : "Reject"}
                                    </Button>
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
            )}

            {/* Vehicle Information */}
            {profile && (
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Vehicle Information</h2>
                    </div>
                    <div className="space-y-4 p-6">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Make</p>
                                <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">{profile.vehicle.make}</p>
                            </div>
                            <div>
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Model</p>
                                <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">{profile.vehicle.model}</p>
                            </div>
                            <div>
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Year</p>
                                <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">{profile.vehicle.year}</p>
                            </div>
                            <div>
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Color</p>
                                <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">{profile.vehicle.color}</p>
                            </div>
                            <div>
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Type</p>
                                <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">{profile.vehicle.type}</p>
                            </div>
                            <div>
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Capacity</p>
                                <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">{profile.vehicle.capacity} passengers</p>
                            </div>
                        </div>
                        {vehicleImageSlots.length > 0 && (
                            <div className="pt-2">
                                <p className="mb-2 text-xs font-medium text-slate-500 dark:text-slate-400">Vehicle Images</p>
                                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                                    {vehicleImageSlots.map((image) => (
                                        <div key={image.key} className="space-y-2">
                                            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                                                {image.label}
                                            </p>
                                            <button
                                                onClick={() => setSelectedImage(image.url)}
                                                className="group relative aspect-[4/3] w-full overflow-hidden rounded-lg border border-slate-200 bg-slate-100 transition hover:border-primary-500 hover:shadow-md dark:border-slate-700 dark:bg-slate-800"
                                            >
                                                <img
                                                    src={image.url}
                                                    alt={image.label}
                                                    className="h-full w-full object-cover transition group-hover:scale-105"
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.style.display = "none";
                                                        const parent = target.parentElement;
                                                        if (parent) {
                                                            parent.innerHTML = `<div class="flex h-full w-full items-center justify-center px-3 text-center text-xs text-slate-400">${image.label}</div>`;
                                                        }
                                                    }}
                                                />
                                                <div className="absolute inset-0 bg-black/0 transition group-hover:bg-black/10" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Location Information */}
            {profile && profile.currentAddress && (
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Current Location</h2>
                    </div>
                    <div className="space-y-4 p-6">
                        <div className="flex items-start gap-4">
                            <MapPinIcon className="mt-0.5 h-5 w-5 text-slate-400" />
                            <div className="flex-1">
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Address</p>
                                <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">{profile.currentAddress}</p>
                            </div>
                        </div>
                        {profile.currentLatitude && profile.currentLongitude && (
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Latitude</p>
                                    <p className="mt-1 font-mono text-sm font-semibold text-slate-900 dark:text-slate-100">{profile.currentLatitude.toFixed(6)}</p>
                                </div>
                                <div>
                                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Longitude</p>
                                    <p className="mt-1 font-mono text-sm font-semibold text-slate-900 dark:text-slate-100">{profile.currentLongitude.toFixed(6)}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Payout Information */}
            {profile && profile.payoutInfo && canViewPayoutInfo ? (
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Payout Information</h2>
                    </div>
                    <div className="space-y-4 p-6">
                        <div className="flex items-start gap-4">
                            <CurrencyDollarIcon className="mt-0.5 h-5 w-5 text-slate-400" />
                            <div className="flex-1">
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Account Name</p>
                                <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">{profile.payoutInfo.accountName}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <CurrencyDollarIcon className="mt-0.5 h-5 w-5 text-slate-400" />
                            <div className="flex-1">
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Account Number</p>
                                <p className="mt-1 font-mono text-sm font-semibold text-slate-900 dark:text-slate-100">{profile.payoutInfo.accountNumber}</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Bank Name</p>
                                <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">{profile.payoutInfo.bankName}</p>
                            </div>
                            <div>
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Bank Code</p>
                                <p className="mt-1 font-mono text-sm font-semibold text-slate-900 dark:text-slate-100">{profile.payoutInfo.bankCode}</p>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}

            {/* Account Status */}
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Account Status</h2>
                </div>
                <div className="space-y-4 p-6">
                    <div>
                        <p className="mb-2 text-xs font-medium text-slate-500 dark:text-slate-400">Status</p>
                        <StatusBadge status={driver.status} />
                    </div>
                    <div>
                        <p className="mb-2 text-xs font-medium text-slate-500 dark:text-slate-400">Deleted</p>
                        <p className={`text-sm font-semibold ${driver.deleted ? "text-rose-600 dark:text-rose-400" : "text-emerald-600 dark:text-emerald-400"}`}>
                            {driver.deleted ? "Yes" : "No"}
                        </p>
                    </div>
                    {driver.gender && (
                        <div>
                            <p className="mb-2 text-xs font-medium text-slate-500 dark:text-slate-400">Gender</p>
                            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{driver.gender}</p>
                        </div>
                    )}
                    {driver.deletedAt && (
                        <div>
                            <p className="mb-2 text-xs font-medium text-slate-500 dark:text-slate-400">Deleted At</p>
                            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{formatDate(driver.deletedAt)}</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Company/Workplace Information */}
            {(driver.companyName || driver.jobTitle || driver.workEmail || driver.workAddress) && (
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Company/Workplace Information</h2>
                    </div>
                    <div className="space-y-4 p-6">
                        {driver.companyName && (
                            <div className="flex items-start gap-4">
                                <BuildingOfficeIcon className="mt-0.5 h-5 w-5 text-slate-400" />
                                <div className="flex-1">
                                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Company Name</p>
                                    <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">{driver.companyName}</p>
                                </div>
                            </div>
                        )}
                        {driver.jobTitle && (
                            <div className="flex items-start gap-4">
                                <BriefcaseIcon className="mt-0.5 h-5 w-5 text-slate-400" />
                                <div className="flex-1">
                                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Job Title</p>
                                    <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">{driver.jobTitle}</p>
                                </div>
                            </div>
                        )}
                        {driver.workEmail && (
                            <div className="flex items-start gap-4">
                                <EnvelopeIcon className="mt-0.5 h-5 w-5 text-slate-400" />
                                <div className="flex-1">
                                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Work Email</p>
                                    <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">{driver.workEmail}</p>
                                </div>
                            </div>
                        )}
                        {driver.workAddress && (
                            <div className="flex items-start gap-4">
                                <MapPinIcon className="mt-0.5 h-5 w-5 text-slate-400" />
                                <div className="flex-1">
                                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Work Address</p>
                                    <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">{driver.workAddress}</p>
                                </div>
                            </div>
                        )}
                        {driver.workIdFile && (
                            <div className="pt-2">
                                <a href={driver.workIdFile} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-primary-600 hover:text-primary-500">
                                    View Work ID Document →
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Security & Settings */}
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Security & Settings</h2>
                </div>
                <div className="space-y-4 p-6">
                    <div className="flex items-center gap-2">
                        <ShieldCheckIcon className="h-5 w-5 text-slate-400" />
                        <div className="flex-1">
                            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Two-Factor Authentication</p>
                            <p className={`mt-1 text-sm font-semibold ${driver.twoFactorEnabled ? "text-emerald-600 dark:text-emerald-400" : "text-slate-600 dark:text-slate-400"}`}>
                                {driver.twoFactorEnabled ? "Enabled" : "Disabled"}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Profile Completeness */}
            {(driver.missingRequiredFields.length > 0 || driver.missingOptionalFields.length > 0) && (
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Profile Completeness</h2>
                    </div>
                    <div className="space-y-4 p-6">
                        {driver.missingRequiredFields.length > 0 && (
                            <div>
                                <div className="mb-2 flex items-center gap-2">
                                    <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" />
                                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Missing Required Fields</p>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {driver.missingRequiredFields.map((field, index) => (
                                        <span key={index} className="inline-flex rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                                            {field.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                        {driver.missingOptionalFields.length > 0 && (
                            <div>
                                <div className="mb-2 flex items-center gap-2">
                                    <InformationCircleIcon className="h-5 w-5 text-slate-400" />
                                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Missing Optional Fields</p>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {driver.missingOptionalFields.map((field, index) => (
                                        <span key={index} className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                                            {field.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Additional Information */}
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Additional Information</h2>
                </div>
                <div className="space-y-4 p-6">
                    <div>
                        <p className="mb-2 text-xs font-medium text-slate-500 dark:text-slate-400">Driver ID</p>
                        <p className="font-mono text-sm font-semibold text-slate-900 dark:text-slate-100">{driver.id}</p>
                    </div>
                    <div>
                        <p className="mb-2 text-xs font-medium text-slate-500 dark:text-slate-400">Account Created</p>
                        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{formatDate(driver.createdAt)}</p>
                    </div>
                    <div>
                        <p className="mb-2 text-xs font-medium text-slate-500 dark:text-slate-400">Last Updated</p>
                        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{formatDate(driver.updatedAt)}</p>
                    </div>
                    <div>
                        <p className="mb-2 text-xs font-medium text-slate-500 dark:text-slate-400">Last Login</p>
                        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{formatDate(driver.lastLoginAt)}</p>
                    </div>
                </div>
            </div>
        </div>

    </>
    );
};

// ============================================================================
// Rides Tab
// ============================================================================

const RideSummaryCards = ({ summary }: { summary: DriverRideSummary }) => (
    <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Total Rides</p>
            <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">{summary.totalRides}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Completed</p>
            <p className="mt-1 text-2xl font-bold text-emerald-600 dark:text-emerald-400">{summary.completedRides}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Cancelled</p>
            <p className="mt-1 text-2xl font-bold text-rose-600 dark:text-rose-400">{summary.cancelledRides}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Total Revenue</p>
            <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">{formatCurrency(summary.totalRevenue)}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">In Progress</p>
            <p className="mt-1 text-2xl font-bold text-blue-600 dark:text-blue-400">{summary.inProgressRides}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Available</p>
            <p className="mt-1 text-2xl font-bold text-emerald-600 dark:text-emerald-400">{summary.availableRides}</p>
        </div>
        <div className="col-span-2 rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Total Passengers Served</p>
            <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">{summary.totalPassengersServed}</p>
        </div>
    </div>
);

const RidesTab = ({ driverId }: { driverId: string }) => {
    const [page, setPage] = useState(0);

    const { data, isLoading, error } = useQuery({
        queryKey: ["driverRides", driverId, page],
        queryFn: () => getDriverRides(driverId, page),
        enabled: !!driverId,
    });

    const ridesData = data?.data;

    return (
        <div className="space-y-0">
            {ridesData?.summary && <RideSummaryCards summary={ridesData.summary} />}

            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Ride Offers</h2>
                        {ridesData && (
                            <span className="text-sm text-slate-500 dark:text-slate-400">
                                {ridesData.totalElements} total
                            </span>
                        )}
                    </div>
                </div>

                {isLoading ? (
                    <LoadingSpinner />
                ) : error ? (
                    <ErrorState message="Failed to load rides." />
                ) : !ridesData?.rides?.length ? (
                    <EmptyState icon={TruckIcon} title="No rides found" subtitle="This driver hasn't created any ride offers yet." />
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
                                <thead className="bg-slate-50 dark:bg-slate-800/60">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Ride</th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Route</th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Schedule</th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Seats</th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Status</th>
                                        <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Price/Seat</th>
                                        <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Revenue</th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Bookings</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                                    {ridesData.rides.map((ride: DriverRide) => (
                                        <tr key={ride.rideOfferId} className="transition hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <p className="font-mono text-sm font-medium text-slate-900 dark:text-slate-100">{ride.rideCode}</p>
                                                <p className="text-xs text-slate-500 dark:text-slate-400">{ride.vehicleType}</p>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="max-w-[200px]">
                                                    <div className="flex items-start gap-1.5">
                                                        <MapPinIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
                                                        <p className="truncate text-xs text-slate-700 dark:text-slate-300">{ride.originAddress}</p>
                                                    </div>
                                                    <div className="mt-1 flex items-start gap-1.5">
                                                        <MapPinIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-500" />
                                                        <p className="truncate text-xs text-slate-700 dark:text-slate-300">{ride.destinationAddress}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <p className="text-sm text-slate-900 dark:text-slate-100">{formatShortDate(ride.departureDate)}</p>
                                                <p className="text-xs text-slate-500 dark:text-slate-400">{ride.departureTime}</p>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <p className="text-sm text-slate-900 dark:text-slate-100">{ride.bookedSeats}/{ride.totalSeats}</p>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <StatusBadge status={ride.status} />
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-right">
                                                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                                    {formatCurrency(ride.pricePerSeat, ride.pricePerSeatCurrency)}
                                                </p>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-right">
                                                <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                                                    {formatCurrency(ride.revenue)}
                                                </p>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <p className="text-sm text-slate-900 dark:text-slate-100">{ride.bookingCount}</p>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            currentPage={ridesData.currentPage}
                            totalPages={ridesData.totalPages}
                            totalElements={ridesData.totalElements}
                            onPageChange={setPage}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

// ============================================================================
// Trips Tab
// ============================================================================

const TripSummaryCards = ({ summary }: { summary: DriverTripSummary }) => (
    <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Completed Trips</p>
            <p className="mt-1 text-2xl font-bold text-emerald-600 dark:text-emerald-400">{summary.completedTrips}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Cancelled Trips</p>
            <p className="mt-1 text-2xl font-bold text-rose-600 dark:text-rose-400">{summary.cancelledTrips}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Total Earned</p>
            <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">{formatCurrency(summary.totalEarned)}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Avg Rating from Passengers</p>
            <div className="mt-1">
                <RatingStars rating={summary.avgRatingFromPassengers} />
            </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Avg Rating Given</p>
            <div className="mt-1">
                <RatingStars rating={summary.avgRatingGivenToPassengers} />
            </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Unique Passengers</p>
            <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">{summary.totalUniquePassengers}</p>
        </div>
    </div>
);

const TripsTab = ({ driverId }: { driverId: string }) => {
    const [page, setPage] = useState(0);

    const { data, isLoading, error } = useQuery({
        queryKey: ["driverTrips", driverId, page],
        queryFn: () => getDriverTrips(driverId, page),
        enabled: !!driverId,
    });

    const tripsData = data?.data;

    return (
        <div className="space-y-0">
            {tripsData?.summary && <TripSummaryCards summary={tripsData.summary} />}

            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Trip History</h2>
                        {tripsData && (
                            <span className="text-sm text-slate-500 dark:text-slate-400">
                                {tripsData.totalElements} total
                            </span>
                        )}
                    </div>
                </div>

                {isLoading ? (
                    <LoadingSpinner />
                ) : error ? (
                    <ErrorState message="Failed to load trips." />
                ) : !tripsData?.trips?.length ? (
                    <EmptyState icon={TicketIcon} title="No trips found" subtitle="This driver hasn't completed any trips yet." />
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
                                <thead className="bg-slate-50 dark:bg-slate-800/60">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Trip</th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Route</th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Passenger</th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Status</th>
                                        <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Fare</th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Details</th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Ratings</th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Date</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                                    {tripsData.trips.map((trip: DriverTrip) => (
                                        <tr key={trip.tripId} className="transition hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <p className="font-mono text-sm font-medium text-slate-900 dark:text-slate-100">
                                                    {trip.tripId.slice(0, 8)}...
                                                </p>
                                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                                    {trip.numberOfSeats} seat{trip.numberOfSeats > 1 ? "s" : ""}
                                                </p>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="max-w-[200px]">
                                                    <div className="flex items-start gap-1.5">
                                                        <MapPinIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
                                                        <p className="truncate text-xs text-slate-700 dark:text-slate-300">{trip.pickupAddress}</p>
                                                    </div>
                                                    <div className="mt-1 flex items-start gap-1.5">
                                                        <MapPinIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-500" />
                                                        <p className="truncate text-xs text-slate-700 dark:text-slate-300">{trip.dropoffAddress}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <p className="text-sm text-slate-900 dark:text-slate-100">{trip.passengerName}</p>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <StatusBadge status={trip.status} />
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-right">
                                                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                                    {formatCurrency(trip.finalFare ?? trip.estimatedFare, trip.finalFareCurrency || trip.estimatedFareCurrency)}
                                                </p>
                                                {trip.finalFare !== null && trip.finalFare !== trip.estimatedFare && (
                                                    <p className="text-xs text-slate-400 line-through">
                                                        {formatCurrency(trip.estimatedFare, trip.estimatedFareCurrency)}
                                                    </p>
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <div className="text-xs text-slate-600 dark:text-slate-400">
                                                    {trip.distanceKm !== null && <p>{trip.distanceKm} km</p>}
                                                    {trip.durationMinutes !== null && <p>{trip.durationMinutes} min</p>}
                                                </div>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <div className="space-y-1">
                                                    {trip.driverRating !== null && (
                                                        <div>
                                                            <p className="text-[10px] text-slate-400">From passenger</p>
                                                            <RatingStars rating={trip.driverRating} />
                                                        </div>
                                                    )}
                                                    {trip.passengerRating !== null && (
                                                        <div>
                                                            <p className="text-[10px] text-slate-400">To passenger</p>
                                                            <RatingStars rating={trip.passengerRating} />
                                                        </div>
                                                    )}
                                                    {trip.driverRating === null && trip.passengerRating === null && (
                                                        <span className="text-xs text-slate-400">{"\u2014"}</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <p className="text-sm text-slate-700 dark:text-slate-300">
                                                    {formatShortDate(trip.completedAt || trip.startedAt || trip.createdAt)}
                                                </p>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            currentPage={tripsData.currentPage}
                            totalPages={tripsData.totalPages}
                            totalElements={tripsData.totalElements}
                            onPageChange={setPage}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

// ============================================================================
// Wallet Tab
// ============================================================================

const WalletSummaryCards = ({ walletData }: { walletData: { availableBalance: number; totalEarned: number; totalPaidOut: number; currency: string; summary: DriverWalletSummary } }) => (
    <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Available Balance</p>
            <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">{formatCurrency(walletData.availableBalance, walletData.currency)}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Total Earned</p>
            <p className="mt-1 text-2xl font-bold text-emerald-600 dark:text-emerald-400">{formatCurrency(walletData.totalEarned, walletData.currency)}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Total Paid Out</p>
            <p className="mt-1 text-2xl font-bold text-blue-600 dark:text-blue-400">{formatCurrency(walletData.totalPaidOut, walletData.currency)}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Completed Payouts</p>
            <p className="mt-1 text-2xl font-bold text-emerald-600 dark:text-emerald-400">{walletData.summary.completedPayouts}</p>
            <p className="mt-1 text-xs text-slate-400">{formatCurrency(walletData.summary.totalCompletedPayoutAmount, walletData.currency)}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Pending / Failed</p>
            <p className="mt-1 text-2xl font-bold text-amber-600 dark:text-amber-400">{walletData.summary.pendingPayouts}</p>
            <p className="mt-1 text-xs text-slate-400">{walletData.summary.failedPayouts} failed</p>
        </div>
    </div>
);

const WalletTab = ({ driverId }: { driverId: string }) => {
    const [page, setPage] = useState(0);

    const { data, isLoading, error } = useQuery({
        queryKey: ["driverWallet", driverId, page],
        queryFn: () => getDriverWallet(driverId, page),
        enabled: !!driverId,
    });

    const walletData = data?.data;

    return (
        <div className="space-y-0">
            {walletData?.summary && <WalletSummaryCards walletData={walletData} />}

            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Payout History</h2>
                        {walletData && (
                            <span className="text-sm text-slate-500 dark:text-slate-400">
                                {walletData.totalPayouts} total
                            </span>
                        )}
                    </div>
                </div>

                {isLoading ? (
                    <LoadingSpinner />
                ) : error ? (
                    <ErrorState message="Failed to load wallet." />
                ) : !walletData?.payouts?.length ? (
                    <EmptyState icon={WalletIcon} title="No payouts" subtitle="This driver has no payout history yet." />
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
                                <thead className="bg-slate-50 dark:bg-slate-800/60">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Payout ID</th>
                                        <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Amount</th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Status</th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Reference</th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Initiated</th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Processed</th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Failure Reason</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                                    {walletData.payouts.map((payout: DriverPayout) => (
                                        <tr key={payout.payoutId} className="transition hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <p className="font-mono text-sm font-medium text-slate-900 dark:text-slate-100">
                                                    {payout.payoutId.slice(0, 12)}...
                                                </p>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-right">
                                                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                                    {formatCurrency(payout.amount, payout.currency)}
                                                </p>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <StatusBadge status={payout.status} />
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <p className="font-mono text-xs text-slate-500 dark:text-slate-400">
                                                    {payout.transferReference || "\u2014"}
                                                </p>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <p className="text-sm text-slate-700 dark:text-slate-300">
                                                    {formatShortDate(payout.initiatedAt)}
                                                </p>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <p className="text-sm text-slate-700 dark:text-slate-300">
                                                    {formatShortDate(payout.processedAt)}
                                                </p>
                                            </td>
                                            <td className="px-6 py-4">
                                                <p className="max-w-[150px] truncate text-sm text-rose-500">
                                                    {payout.failureReason || "\u2014"}
                                                </p>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            currentPage={walletData.currentPage}
                            totalPages={walletData.totalPages}
                            totalElements={walletData.totalPayouts}
                            onPageChange={setPage}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

// ============================================================================
// Main Component
// ============================================================================

export const DriverDetailsRoute = () => {
    const { driverId } = useParams<{ driverId: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { hasAnyPermission, hasPermission } = usePermissions();
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<TabKey>("profile");
    const [flashMessage, setFlashMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
    const [statusModal, setStatusModal] = useState<{
        action: "reactivate" | "suspend";
        label: string;
    } | null>(null);
    const [statusReason, setStatusReason] = useState("");
    const [statusError, setStatusError] = useState("");
    const [banModal, setBanModal] = useState(false);
    const [banReason, setBanReason] = useState("");
    const [banError, setBanError] = useState("");
    const [pushModal, setPushModal] = useState(false);
    const [pushTitle, setPushTitle] = useState("Message from Ride2Five Admin");
    const [pushMessage, setPushMessage] = useState("");
    const [pushError, setPushError] = useState("");
    const {
        presence,
        connectionState: presenceConnectionState,
        error: presenceError,
        lastUpdatedAt: presenceLastUpdatedAt,
        refresh: refreshPresence,
    } = useUserPresenceLookup(driverId ?? null);
    const canViewRides = hasPermission(SECTION_PERMISSIONS.DRIVER_RIDES[0]);
    const canViewTrips = hasPermission(SECTION_PERMISSIONS.DRIVER_TRIPS[0]);
    const canViewWallet = hasAnyPermission([
        ...SECTION_PERMISSIONS.DRIVER_WALLET,
        ...SECTION_PERMISSIONS.DRIVER_PAYOUTS_VIEW,
    ]);
    const canModerateDrivers = hasPermission(SECTION_PERMISSIONS.DRIVERS_MODERATE[0]);
    const canSendPush = hasPermission(SECTION_PERMISSIONS.PUSH_NOTIFICATIONS[0]);
    const canReviewRoadWorthiness = hasPermission(
        SECTION_PERMISSIONS.DRIVER_ROAD_WORTHINESS_REVIEW[0]
    );
    const canViewPayoutInfo = hasPermission(SECTION_PERMISSIONS.DRIVER_PAYOUTS_VIEW[0]);
    const visibleTabs = TABS.filter((tab) =>
        !tab.permissions ? true : hasAnyPermission(tab.permissions)
    );

    const { data, isLoading, error } = useQuery({
        queryKey: ["driverDetails", driverId],
        queryFn: () => getDriverDetails(driverId!),
        enabled: !!driverId
    });

    useEffect(() => {
        if (!flashMessage) return;
        const timer = setTimeout(() => setFlashMessage(null), 5000);
        return () => clearTimeout(timer);
    }, [flashMessage]);

    useEffect(() => {
        if (!visibleTabs.some((tab) => tab.key === activeTab)) {
            setActiveTab(visibleTabs[0]?.key ?? "profile");
        }
    }, [activeTab, visibleTabs]);

    const roadWorthinessReviewMutation = useMutation({
        mutationFn: ({
            action,
            reason
        }: {
            action: RoadWorthinessReviewAction;
            reason?: string;
        }) => reviewDriverRoadWorthiness(driverId!, { action, reason }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ["driverDetails", driverId] });
        }
    });

    const statusMutation = useMutation({
        mutationFn: ({
            action,
            reason,
        }: {
            action: "reactivate" | "suspend";
            reason?: string;
        }) => {
            if (action === "reactivate") return reactivateDriver(driverId!, reason);
            return suspendDriver(driverId!, reason);
        },
        onSuccess: async (response, { action }) => {
            log.info("Driver status updated", { driverId, action });
            await queryClient.invalidateQueries({ queryKey: ["driverDetails", driverId] });
            await queryClient.invalidateQueries({ queryKey: ["drivers"] });
            setStatusModal(null);
            setStatusReason("");
            setStatusError("");
            setFlashMessage({
                type: "success",
                text: buildApprovalSubmissionFeedback(response),
            });
        },
        onError: (err, { action }) => {
            const msg = extractApiError(err);
            log.error("Failed to update driver status", { driverId, action, error: msg });
            setStatusError(msg);
        },
    });

    const banMutation = useMutation({
        mutationFn: (reason?: string) => banDriver(driverId!, reason),
        onSuccess: async (response) => {
            log.info("Driver permanently banned", { driverId });
            await queryClient.invalidateQueries({ queryKey: ["driverDetails", driverId] });
            await queryClient.invalidateQueries({ queryKey: ["drivers"] });
            setBanModal(false);
            setBanReason("");
            setBanError("");
            setFlashMessage({
                type: "success",
                text: buildApprovalSubmissionFeedback(response),
            });
        },
        onError: (err) => {
            const msg = extractApiError(err);
            log.error("Failed to ban driver", { driverId, error: msg });
            setBanError(msg);
        },
    });

    const pushMutation = useMutation({
        mutationFn: () =>
            sendPushNotificationToUser({
                userId: driverId!,
                title: pushTitle.trim(),
                body: pushMessage.trim(),
                type: "SYSTEM_ALERT",
                data: {
                    screen: "driver_details",
                    source: "admin_panel",
                },
                highPriority: true,
            }),
        onSuccess: (response) => {
            log.info("Push notification sent to driver", { driverId });
            setPushModal(false);
            setPushTitle("Message from Ride2Five Admin");
            setPushMessage("");
            setPushError("");
            setFlashMessage({
                type: "success",
                text: buildApprovalSubmissionFeedback(response),
            });
        },
        onError: (err) => {
            const msg = extractApiError(err);
            log.error("Failed to send push notification", { driverId, error: msg });
            setPushError(msg);
        },
    });

    if (isLoading) {
        return (
            <div className="flex h-96 items-center justify-center">
                <div className="text-center">
                    <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary-500 border-r-transparent"></div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">Loading driver details...</p>
                </div>
            </div>
        );
    }

    if (error || !data?.data) {
        return (
            <div className="flex h-96 items-center justify-center">
                <div className="text-center">
                    <p className="mb-2 text-lg font-semibold text-slate-900 dark:text-slate-100">Failed to load driver details</p>
                    <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">Please try again later.</p>
                    <button
                        onClick={() => navigate("/drivers")}
                        className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-primary-600"
                    >
                        Back to Drivers
                    </button>
                </div>
            </div>
        );
    }

    const driver: DriverDetails = data.data;
    const profile = driver.driverProfile;
    const statusActions = () => {
        if (!canModerateDrivers) {
            return [];
        }
        const actions = [];
        if (driver.status === "ACTIVE" || driver.status === "INACTIVE") {
            actions.push({ action: "suspend" as const, label: "Suspend", color: "amber" });
        }
        if (driver.status === "SUSPENDED") {
            actions.push({ action: "reactivate" as const, label: "Unsuspend", color: "emerald" });
        }
        return actions;
    };

    return (
        <div className="space-y-6">
            {flashMessage && (
                <div
                    className={`flex items-center justify-between rounded-xl border px-4 py-3 text-sm font-medium ${
                        flashMessage.type === "success"
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400"
                            : "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-400"
                    }`}
                >
                    <div className="flex items-center gap-2">
                        {flashMessage.type === "success" ? (
                            <CheckCircleIcon className="h-4 w-4 shrink-0" />
                        ) : (
                            <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
                        )}
                        <span>{flashMessage.text}</span>
                    </div>
                    <button
                        onClick={() => setFlashMessage(null)}
                        className="ml-4 rounded p-0.5 opacity-60 transition hover:opacity-100"
                        aria-label="Dismiss"
                    >
                        <XCircleIcon className="h-4 w-4" />
                    </button>
                </div>
            )}

            {/* Header */}
            <div className="flex items-center justify-between">
                <button
                    onClick={() => navigate("/drivers")}
                    className="flex items-center gap-2 text-sm font-medium text-slate-600 transition hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
                >
                    <ArrowLeftIcon className="h-5 w-5" />
                    Back to Drivers
                </button>
                <div className="flex items-center gap-2">
                    {canSendPush ? (
                        <button
                            onClick={() => {
                                setPushModal(true);
                                setPushTitle("Message from Ride2Five Admin");
                                setPushMessage("");
                                setPushError("");
                            }}
                            className="flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                        >
                            <PaperAirplaneIcon className="h-4 w-4" />
                            Send Push Notification
                        </button>
                    ) : null}
                    {statusActions().map(({ action, label, color }) => (
                        <button
                            key={action}
                            onClick={() => {
                                setStatusModal({ action, label });
                                setStatusReason("");
                                setStatusError("");
                            }}
                            className={`flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium transition ${
                                color === "emerald"
                                    ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-400 dark:hover:bg-emerald-900/20"
                                    : "border-amber-200 text-amber-700 hover:bg-amber-50 dark:border-amber-800 dark:text-amber-400 dark:hover:bg-amber-900/20"
                            }`}
                        >
                            {color === "emerald" ? (
                                <PowerIcon className="h-4 w-4" />
                            ) : (
                                <NoSymbolIcon className="h-4 w-4" />
                            )}
                            {label}
                        </button>
                    ))}
                    {canModerateDrivers && driver.status !== "BANNED" && !driver.deleted ? (
                        <button
                            onClick={() => {
                                setBanModal(true);
                                setBanReason("");
                                setBanError("");
                            }}
                            className="flex items-center gap-2 rounded-xl border border-rose-200 px-4 py-2 text-sm font-medium text-rose-600 transition hover:bg-rose-50 dark:border-rose-800 dark:text-rose-400 dark:hover:bg-rose-900/20"
                        >
                            <TrashIcon className="h-4 w-4" />
                            Ban (Permanent)
                        </button>
                    ) : null}
                </div>
            </div>

            {/* Profile Header Card */}
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-8 py-6">
                    <div className="flex items-center gap-6">
                        {driver.profilePicture ? (
                            <img
                                src={driver.profilePicture}
                                alt={`${driver.firstName} ${driver.lastName}`}
                                className="h-20 w-20 cursor-zoom-in rounded-full object-cover ring-4 ring-white/20 transition-transform hover:scale-105"
                                onClick={() => setSelectedImage(driver.profilePicture)}
                            />
                        ) : (
                            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white/20 text-2xl font-bold text-white backdrop-blur-sm">
                                {driver.firstName[0]}
                                {driver.lastName[0]}
                            </div>
                        )}
                        <div className="flex-1">
                            <div className="mb-2">
                                <h1 className="text-2xl font-bold text-white">
                                    {driver.firstName} {driver.lastName}
                                </h1>
                            </div>
                            <p className="text-sm text-primary-100">Driver ID: {driver.id}</p>
                            {profile && (
                                <div className="mt-2 flex items-center gap-4">
                                    <div className="flex items-center gap-1">
                                        <StarIcon className="h-4 w-4 text-yellow-300" />
                                        <span className="text-sm font-semibold text-white">{profile.rating.toFixed(1)}</span>
                                    </div>
                                    <span className="text-sm text-primary-100">{profile.totalTrips} trips</span>
                                    <span className={`text-sm font-medium ${profile.isAvailable ? "text-emerald-200" : "text-slate-300"}`}>
                                        {profile.isAvailable ? "Available" : "Unavailable"}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Profile Stats */}
                <div className="grid grid-cols-1 gap-6 border-t border-slate-200 p-8 dark:border-slate-800 md:grid-cols-4">
                    <div>
                        <div className="mb-2 flex items-center gap-2">
                            <UserCircleIcon className="h-5 w-5 text-slate-400" />
                            <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Profile Completion</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                                <div
                                    className="h-full rounded-full bg-primary-500 transition-all"
                                    style={{ width: `${driver.profileCompletionPercentage}%` }}
                                ></div>
                            </div>
                            <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                {driver.profileCompletionPercentage}%
                            </span>
                        </div>
                    </div>
                    <div>
                        <div className="mb-2 flex items-center gap-2">
                            <ClockIcon className="h-5 w-5 text-slate-400" />
                            <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Last Login</span>
                        </div>
                        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{formatDate(driver.lastLoginAt)}</p>
                    </div>
                    <div>
                        <div className="mb-2 flex items-center gap-2">
                            <CalendarIcon className="h-5 w-5 text-slate-400" />
                            <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Joined</span>
                        </div>
                        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{formatDate(driver.createdAt)}</p>
                    </div>
                    {profile && (
                        <div>
                            <div className="mb-2 flex items-center gap-2">
                                <ClockIcon className="h-5 w-5 text-slate-400" />
                                <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Last Active</span>
                            </div>
                            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{formatDate(profile.lastActiveAt)}</p>
                        </div>
                    )}
                </div>
            </div>

            <PresencePanel
                presence={presence}
                connectionState={presenceConnectionState}
                error={presenceError}
                lastUpdatedAt={presenceLastUpdatedAt}
                onRefresh={refreshPresence}
            />

            {/* Tabs */}
            <div className="border-b border-slate-200 dark:border-slate-800">
                <nav className="-mb-px flex gap-6 overflow-x-auto" aria-label="Tabs">
                    {visibleTabs.map(({ key, label, icon: Icon }) => (
                        <button
                            key={key}
                            onClick={() => setActiveTab(key)}
                            className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-1 pb-3 pt-1 text-sm font-medium transition ${activeTab === key
                                    ? "border-primary-500 text-primary-600 dark:text-primary-400"
                                    : "border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
                                }`}
                        >
                            <Icon className="h-5 w-5" />
                            {label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Tab Content */}
            {activeTab === "profile" && (
                <ProfileTab
                    driver={driver}
                    profile={profile}
                    setSelectedImage={setSelectedImage}
                    canReviewRoadWorthiness={canReviewRoadWorthiness}
                    canViewPayoutInfo={canViewPayoutInfo}
                    onRoadWorthinessReview={(action, reason) =>
                        roadWorthinessReviewMutation.mutateAsync({ action, reason })
                    }
                    roadWorthinessReviewError={
                        roadWorthinessReviewMutation.isError
                            ? "Failed to submit road worthiness review. Please try again."
                            : null
                    }
                />
            )}
            {activeTab === "rides" && driverId && canViewRides ? <RidesTab driverId={driverId} /> : null}
            {activeTab === "trips" && driverId && canViewTrips ? <TripsTab driverId={driverId} /> : null}
            {activeTab === "wallet" && driverId && canViewWallet ? <WalletTab driverId={driverId} /> : null}

            {selectedImage && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
                    onClick={() => setSelectedImage(null)}
                >
                    <button
                        onClick={() => setSelectedImage(null)}
                        className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white backdrop-blur-sm transition hover:bg-white/20"
                        aria-label="Close"
                    >
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                    <div className="relative max-h-[90vh] max-w-[90vw]">
                        <img
                            src={selectedImage}
                            alt={`${driver.firstName} ${driver.lastName} preview`}
                            className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain shadow-2xl"
                            onClick={(event) => event.stopPropagation()}
                            onError={(event) => {
                                const target = event.target as HTMLImageElement;
                                target.src = "";
                                target.alt = "Failed to load image";
                            }}
                        />
                    </div>
                </div>
            )}

            {statusModal && (
                <ModalOverlay
                    onClose={() => {
                        setStatusModal(null);
                        setStatusError("");
                    }}
                >
                    <ModalCard>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                            {statusModal.label} Driver
                        </h3>
                        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                            {driver.firstName} {driver.lastName}
                        </p>
                        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                            This change will be submitted to the super admin approval queue before
                            it takes effect.
                        </p>
                        <div className="mt-4 space-y-4">
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Reason <span className="text-xs text-slate-400">(optional)</span>
                                </label>
                                <textarea
                                    rows={3}
                                    value={statusReason}
                                    onChange={(event) => {
                                        setStatusReason(event.target.value);
                                        if (statusError) setStatusError("");
                                    }}
                                    placeholder={`Reason for ${statusModal.label.toLowerCase()}...`}
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                />
                            </div>
                            <InlineError message={statusError} />
                        </div>
                        <div className="mt-6 flex gap-3">
                            <button
                                onClick={() => {
                                    setStatusModal(null);
                                    setStatusError("");
                                }}
                                disabled={statusMutation.isPending}
                                className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() =>
                                    statusMutation.mutate({
                                        action: statusModal.action,
                                        reason: statusReason || undefined,
                                    })
                                }
                                disabled={statusMutation.isPending}
                                className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-medium text-white transition disabled:opacity-50 ${
                                    statusModal.action === "suspend"
                                        ? "bg-amber-600 hover:bg-amber-700"
                                        : "bg-emerald-600 hover:bg-emerald-700"
                                }`}
                            >
                                {statusMutation.isPending
                                    ? "Submitting..."
                                    : `Submit ${statusModal.label} Request`}
                            </button>
                        </div>
                    </ModalCard>
                </ModalOverlay>
            )}

            {banModal && (
                <ModalOverlay
                    onClose={() => {
                        setBanModal(false);
                        setBanError("");
                    }}
                >
                    <ModalCard>
                        <div className="mb-4 flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-100 dark:bg-rose-900/30">
                                <TrashIcon className="h-5 w-5 text-rose-600 dark:text-rose-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                                Submit Driver Ban Request
                            </h3>
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-300">
                            Are you sure you want to permanently ban{" "}
                            <strong>
                                {driver.firstName} {driver.lastName}
                            </strong>
                            ? This request will go to the super admin approval queue first. Once
                            approved, the account cannot be reactivated and the email/phone cannot
                            be reused.
                        </p>
                        <div className="mt-4 space-y-4">
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Reason <span className="text-xs text-slate-400">(optional)</span>
                                </label>
                                <textarea
                                    rows={3}
                                    value={banReason}
                                    onChange={(event) => {
                                        setBanReason(event.target.value);
                                        if (banError) setBanError("");
                                    }}
                                    placeholder="Reason for permanent ban..."
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                />
                            </div>
                            <InlineError message={banError} />
                        </div>
                        <div className="mt-6 flex gap-3">
                            <button
                                onClick={() => {
                                    setBanModal(false);
                                    setBanError("");
                                }}
                                disabled={banMutation.isPending}
                                className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => banMutation.mutate(banReason || undefined)}
                                disabled={banMutation.isPending}
                                className="flex-1 rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-rose-700 disabled:opacity-50"
                            >
                                {banMutation.isPending
                                    ? "Submitting..."
                                    : "Submit Ban Request"}
                            </button>
                        </div>
                    </ModalCard>
                </ModalOverlay>
            )}

            {pushModal && (
                <ModalOverlay
                    onClose={() => {
                        setPushModal(false);
                        setPushTitle("Message from Ride2Five Admin");
                        setPushMessage("");
                        setPushError("");
                    }}
                >
                    <ModalCard>
                        <div className="mb-4 flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30">
                                <PaperAirplaneIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                                    Submit Push Notification
                                </h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                    To: {driver.firstName} {driver.lastName} &middot; {driver.id}
                                </p>
                            </div>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Title
                                </label>
                                <input
                                    type="text"
                                    value={pushTitle}
                                    onChange={(event) => {
                                        setPushTitle(event.target.value);
                                        if (pushError) setPushError("");
                                    }}
                                    placeholder="Push notification title"
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                />
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Message
                                </label>
                                <textarea
                                    rows={4}
                                    value={pushMessage}
                                    onChange={(event) => {
                                        setPushMessage(event.target.value);
                                        if (pushError) setPushError("");
                                    }}
                                    placeholder="Type your message..."
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                />
                                <p className="mt-1 text-right text-xs text-slate-400">
                                    {pushMessage.length} chars
                                </p>
                                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                    The driver will receive this targeted push notification after
                                    it is approved.
                                </p>
                            </div>
                            <InlineError message={pushError} />
                        </div>
                        <div className="mt-6 flex gap-3">
                            <button
                                onClick={() => {
                                    setPushModal(false);
                                    setPushTitle("Message from Ride2Five Admin");
                                    setPushMessage("");
                                    setPushError("");
                                }}
                                disabled={pushMutation.isPending}
                                className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => pushMutation.mutate()}
                                disabled={
                                    pushMutation.isPending ||
                                    !pushTitle.trim() ||
                                    !pushMessage.trim()
                                }
                                className="flex-1 rounded-xl bg-primary-500 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-primary-600 disabled:opacity-50"
                            >
                                {pushMutation.isPending
                                    ? "Submitting..."
                                    : "Submit Push Request"}
                            </button>
                        </div>
                    </ModalCard>
                </ModalOverlay>
            )}
        </div>
    );
};
